import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from './api';
import { clearToken, getToken, setToken } from './auth';
import { AUTH_EXPIRED_EVENT } from './api';

const AuthContext = createContext(null);
const isUnauthorizedStatus = (status) => status === 401 || status === 403;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const bootstrap = async () => {
      const token = getToken();

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await api.get('/auth/me');
        setUser(response.data.user);
      } catch (error) {
        const status = Number(error?.response?.status || 0);

        if (isUnauthorizedStatus(status)) {
          clearToken();
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  useEffect(() => {
    const token = getToken();

    if (!token || user) {
      return undefined;
    }

    let cancelled = false;

    const retryBootstrap = async () => {
      try {
        const response = await api.get('/auth/me');
        if (!cancelled) {
          setUser(response.data.user);
        }
      } catch (error) {
        const status = Number(error?.response?.status || 0);

        if (isUnauthorizedStatus(status)) {
          clearToken();
          if (!cancelled) {
            setUser(null);
          }
        }
      }
    };

    const handleReconnectAttempt = () => {
      retryBootstrap();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleReconnectAttempt);
      window.addEventListener('online', handleReconnectAttempt);
      document.addEventListener('visibilitychange', handleReconnectAttempt);
    }

    return () => {
      cancelled = true;

      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', handleReconnectAttempt);
        window.removeEventListener('online', handleReconnectAttempt);
        document.removeEventListener('visibilitychange', handleReconnectAttempt);
      }
    };
  }, [user]);

  useEffect(() => {
    const handleAuthExpired = () => {
      clearToken();
      setUser(null);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(AUTH_EXPIRED_EVENT, handleAuthExpired);
      }
    };
  }, []);

  const login = async (payload, options = {}) => {
    const response = await api.post('/auth/login', payload);
    setToken(response.data.token, options);
    setUser(response.data.user);
    return response.data;
  };

  const register = async (payload) => {
    const response = await api.post('/auth/register', payload);
    setToken(response.data.token);
    setUser(response.data.user);
    return response.data;
  };

  const logout = () => {
    clearToken();
    setUser(null);
  };

  const refreshUser = async () => {
    const response = await api.get('/auth/me');
    setUser(response.data.user);
    return response.data.user;
  };

  const updateUser = (updater) => {
    setUser((prev) => {
      if (typeof updater === 'function') {
        return updater(prev);
      }
      return updater;
    });
  };

  // isAdmin-г хэрэглэгчийн role-оор тодорхойлно
  const value = useMemo(
    () => ({
      user,
      loading,
      login,
      register,
      logout,
      refreshUser,
      updateUser,
      isAdmin: user && user.role === 'admin'
    }),
    [user, loading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
